import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {CanaisVendaFacade} from '../../canais-venda/canais-venda.facade';
import {CanalVendaVM, ProdutoCatalogoCanalVendaVM} from '../../canais-venda/canais-venda.models';
import {ProdutoTabelaPrecosComponent} from '../../compartilhado/produto-tabela-precos/produto-tabela-precos.component';

interface PrecoCanalProduto {
  canal: CanalVendaVM;
  produto: ProdutoCatalogoCanalVendaVM;
  disponivel: boolean;
  herdar: boolean;
  valor: number | null;
  desconto: number;
  promocaoAtiva: boolean;
  alterado: boolean;
  mensagem: string;
  erro: boolean;
}

@Component({
  selector: 'app-precos-canais-produto',
  templateUrl: './precos-canais-produto.component.html',
  styleUrls: ['./precos-canais-produto.component.scss'],
  providers: [CanaisVendaFacade]
})
export class PrecosCanaisProdutoComponent implements OnChanges, OnDestroy {
  @Input() produtoId: number | null = null;
  @Input() podeEditar = false;
  @Input() produtoAtualizado: object | null = null;
  @Input() precoCardapio: number | null = null;
  @Input() descontoCardapio = 0;
  @Input() promocaoCardapioAtiva = false;
  @Input() disponivelCardapio = true;
  @Input() canaisHabilitados = true;
  @Input() editarMeuCardapio = true;
  @Input() empresa: any;
  @Input() produto: any;
  @Input() temModuloTabelaPrecos = false;
  @Input() tabelaPrecosTargetKey: string | null = null;
  @Output() precoCardapioChange = new EventEmitter<number | null>();
  @Output() descontoCardapioChange = new EventEmitter<number>();
  @Output() promocaoCardapioAtivaChange = new EventEmitter<boolean>();
  @Output() possuiPrecoDiferenciadoChange = new EventEmitter<boolean>(true);
  linhas: PrecoCanalProduto[] = [];
  carregando = false;
  salvando = false;
  erro = '';
  percentual = 20;
  private geracao = 0;

  constructor(private facade: CanaisVendaFacade) {}

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.produtoId || changes.canaisHabilitados) void this.carregue();
    else if(changes.produtoAtualizado && !this.salvando) void this.carregue(true);
  }

  ngOnDestroy(): void { this.geracao++; }

  async carregue(preserveEdicoes = false): Promise<void> {
    const geracao = ++this.geracao;
    const anteriores = preserveEdicoes ? this.linhas : [];
    if(!preserveEdicoes) this.linhas = [];
    this.erro = '';
    this.salvando = false;
    if(!this.produtoId || !this.canaisHabilitados) {
      this.carregando = false;
      this.possuiPrecoDiferenciadoChange.emit(false);
      return;
    }
    this.carregando = true;
    try {
      const canais = await this.facade.listeCanaisProduto(this.produtoId);
      if(geracao !== this.geracao) return;
      this.linhas = canais.map(({canal, produto}) => {
        const anterior = anteriores.find(linha => linha.canal.id === canal.id && linha.alterado);
        return anterior ? {...anterior, canal, produto} : this.crieLinha(canal, produto);
      });
      this.possuiPrecoDiferenciadoChange.emit(this.possuiPrecoDiferenciado);
    } catch(erro) {
      if(geracao === this.geracao) {
        this.erro = erro.message || 'Não foi possível carregar os preços por canal.';
        this.possuiPrecoDiferenciadoChange.emit(this.possuiPrecoDiferenciado);
      }
    } finally {
      if(geracao === this.geracao) this.carregando = false;
    }
  }

  private crieLinha(canal: CanalVendaVM, produto: ProdutoCatalogoCanalVendaVM): PrecoCanalProduto {
    return {canal, produto, disponivel: produto.disponivel,
      herdar: produto.preco.herdaBase, valor: produto.preco.canal ?? produto.preco.final,
      desconto: produto.preco.desconto || 0, promocaoAtiva: Number(produto.preco.desconto || 0) > 0,
      alterado: false, mensagem: '', erro: false};
  }

  editavel(linha: PrecoCanalProduto, tabelas?: ProdutoTabelaPrecosComponent): boolean {
    return this.podeEditar && linha.canal.ativo &&
      (linha.canal.plataforma === 'IFOOD' || linha.canal.plataforma === 'FOOD99') && !this.ocupado(tabelas);
  }

  marque(linha: PrecoCanalProduto): void {
    linha.alterado = true;
    linha.mensagem = '';
    linha.erro = false;
  }

  altereDisponibilidade(linha: PrecoCanalProduto, disponivel: boolean): void {
    linha.disponivel = !!disponivel;
    this.marque(linha);
  }

  logoCanal(plataforma: string): string | null {
    if(plataforma === 'IFOOD') return '/assets/icons/logo-ifood.png';
    if(plataforma === 'FOOD99') return '/assets/icons/logo-99food.png';
    return null;
  }

  valorCanal(linha: PrecoCanalProduto): number | null {
    return linha.herdar && !linha.alterado ? linha.produto.preco.final : linha.valor;
  }

  alterePreco(linha: PrecoCanalProduto, valor: number | null): void {
    if(linha.herdar) linha.desconto = 0;
    linha.valor = valor;
    linha.herdar = false;
    this.marque(linha);
  }

  altereDesconto(linha: PrecoCanalProduto, desconto: number): void {
    linha.valor = this.valorCanal(linha);
    linha.desconto = desconto;
    linha.herdar = false;
    this.marque(linha);
  }

  alterePromocaoCanal(linha: PrecoCanalProduto, ativa: boolean): void {
    linha.promocaoAtiva = ativa;
    if(!ativa && linha.desconto !== 0) {
      linha.desconto = 0;
      linha.herdar = false;
      this.marque(linha);
    }
  }

  get exibirColunasPromocao(): boolean {
    return this.promocaoCardapioAtiva || this.linhas.some(linha => linha.promocaoAtiva);
  }

  ocupado(tabelas?: ProdutoTabelaPrecosComponent): boolean {
    return this.salvando || this.carregando || !!(tabelas?.loading || tabelas?.loadingTarget || tabelas?.saving);
  }

  get linhasExibidas(): PrecoCanalProduto[] {
    // Um preco fixo coincidentemente igual nao e heranca e precisa continuar visivel.
    return this.linhas.filter(linha => linha.alterado || !linha.herdar ||
      linha.canal.percentualPrecoPadrao != null);
  }

  get possuiPrecoDiferenciado(): boolean {
    return this.linhasExibidas.some(linha => linha.canal.ativo);
  }

  get podeUsarRegraDoCanal(): boolean {
    return this.linhas.some(linha => this.editavel(linha) && linha.canal.percentualPrecoPadrao !== 0);
  }

  quantidadePendentes(tabelas?: ProdutoTabelaPrecosComponent): number {
    return this.linhas.filter(linha => linha.alterado).length + (tabelas?.quantidadeAlteracoesPrecos || 0);
  }

  get precoFinalCardapio(): number | null {
    if(this.precoCardapio == null) return null;
    return Math.round((Number(this.precoCardapio) - Number(this.descontoCardapio || 0)) * 100) / 100;
  }

  precoFinal(linha: PrecoCanalProduto): number | null {
    const valor = this.valorCanal(linha);
    return valor == null ? null : Math.round((valor - (linha.desconto || 0)) * 100) / 100;
  }

  percentualMaisCaro(linha: PrecoCanalProduto): number | null {
    const precoLocal = this.precoFinalCardapio;
    const precoCanal = this.precoFinal(linha);
    if(precoLocal == null || !Number.isFinite(precoLocal) || precoLocal <= 0 ||
      precoCanal == null || !Number.isFinite(precoCanal) || precoCanal <= precoLocal) return null;
    return (precoCanal - precoLocal) / precoLocal * 100;
  }

  apliquePrecos(herdar: boolean): void {
    if(!herdar && (this.percentual == null || !Number.isFinite(Number(this.percentual)) ||
        this.percentual < -100 || this.percentual > 1000)) {
      this.erro = 'Informe um acréscimo entre -100% e 1000%.';
      return;
    }
    if(this.precoCardapio == null || !Number.isFinite(Number(this.precoCardapio))) {
      this.erro = 'Informe o preço do MeuCardápio antes de repassar aos marketplaces.';
      return;
    }
    this.erro = '';
    const promocaoAtiva = this.promocaoCardapioAtiva;
    this.linhas.filter(linha => this.editavel(linha) &&
      (!herdar || linha.canal.percentualPrecoPadrao !== 0)).forEach(linha => {
      const fator = 1 + Number(herdar ? linha.canal.percentualPrecoPadrao || 0 : this.percentual) / 100;
      linha.herdar = herdar;
      linha.valor = Math.round(Number(this.precoCardapio) * fator * 100) / 100;
      linha.desconto = promocaoAtiva
        ? Math.round(Number(this.descontoCardapio || 0) * fator * 100) / 100 : 0;
      linha.promocaoAtiva = promocaoAtiva;
      this.marque(linha);
    });
  }

  async salve(tabelas?: ProdutoTabelaPrecosComponent): Promise<void> {
    if(this.ocupado(tabelas) || !this.podeEditar || !this.produtoId) return;
    const alteradas = this.linhas.filter(linha => linha.alterado && this.editavel(linha, tabelas));
    for(const linha of alteradas) {
      if(!linha.herdar && (linha.valor == null || !Number.isFinite(linha.valor) || linha.valor < 0 ||
          !Number.isFinite(linha.desconto) || linha.desconto < 0 || linha.desconto > linha.valor)) {
        linha.erro = true;
        linha.mensagem = 'Informe preço e desconto válidos; o desconto não pode superar o preço.';
        return;
      }
    }
    const geracao = this.geracao;
    const produtoId = this.produtoId;
    this.salvando = true;
    try {
      if(tabelas?.quantidadeAlteracoesPrecos) {
        if(!await tabelas.salve()) return;
        if(geracao !== this.geracao) return;
      }
      for(const linha of alteradas) {
        if(geracao !== this.geracao) return;
        try {
          const resultado = await this.facade.configureProduto(linha.canal.id, produtoId, {
            vender: true, disponivel: linha.disponivel,
            preco: linha.herdar ? {modo: 'HERDAR'} : {modo: 'ESPECIFICO', valor: linha.valor, desconto: linha.desconto}
          });
          if(geracao !== this.geracao) return;
          Object.assign(linha, this.crieLinha(linha.canal, resultado.produto), {mensagem: 'Salvo. Não publicado.'});
        } catch(erro) {
          if(geracao !== this.geracao) return;
          linha.erro = true;
          linha.mensagem = erro.message || 'Não foi possível salvar este canal. Tente novamente.';
        }
      }
    } finally {
      if(geracao === this.geracao) {
        this.salvando = false;
        this.possuiPrecoDiferenciadoChange.emit(this.possuiPrecoDiferenciado);
      }
    }
  }
}
